// @givmo/fundraisers-module — the `/org/{org_ref}/fundraisers/{fundraiser_id}/emails/*` reads and the
// writes the email designer makes itself (EMC-WEB10): a person's hand edit (D-EMC-20 revised 2026-10-31),
// the restore behind Undo and Redo, which puts back what the email held before or after a change (the
// assistant's or a hand edit), and the test to the person's own address.
// The committed OpenAPI snapshot carries no `/emails` family, so these use the configured `givmoClient`
// (`api-client.md` § A whole ROUTE the snapshot lacks), inheriting the auth, App Check and error middleware.
//
// ⚠ In flux: Email Campaign Plan EMC-WEB1 adds the Emails tab's own transport (`emails-transport.ts`) over
// the same family; the two fold together when both have landed.

import { givmoClient } from '@givmo/api-client';

function one(orgRef: string, fundraiserId: string, emailId: string): string {
  return `/org/${encodeURIComponent(orgRef)}/fundraisers/${encodeURIComponent(
    fundraiserId,
  )}/emails/${encodeURIComponent(emailId)}`;
}

export async function fetchDesignerEmail(
  orgRef: string,
  fundraiserId: string,
  emailId: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: one(orgRef, fundraiserId, emailId),
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/** The series, for "Email 4 of 9": one page of the largest size is the whole series. */
export async function fetchDesignerSeries(
  orgRef: string,
  fundraiserId: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `/org/${encodeURIComponent(orgRef)}/fundraisers/${encodeURIComponent(fundraiserId)}/emails`,
    query: { 'page[limit]': 100, 'page[offset]': 0 },
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/**
 * The first name the preview is rendered with: a fixed word no real name is, which the page swaps for the
 * "First name" chip the mockups draw (14b–14d). Without it the server fills a sample name ("Alex"), and a
 * person would read a real-looking greeting the email never sends. ASCII, under the route's 40 characters.
 */
export const PREVIEW_FIRST_NAME = 'GivmoFirstNameMergeField';

/** The email as a recipient gets it: the server's render, its greeting's name the merge-field marker. */
export async function fetchDesignerPreview(
  orgRef: string,
  fundraiserId: string,
  emailId: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${one(orgRef, fundraiserId, emailId)}/preview`,
    query: { first_name: PREVIEW_FIRST_NAME },
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/** What Undo puts back: the subject, preview text and message as they were. */
export interface DesignerRestoreBody {
  subject: string;
  preheader: string;
  body_json: Record<string, unknown>;
}

/** A hand edit (D-EMC-20 revised): the subject, the preview text or the whole message, through the screen's
 *  own change route, the one the assistant's change operation runs; only the parts named change. */
export async function saveDesignerEmail(
  orgRef: string,
  fundraiserId: string,
  emailId: string,
  body: Partial<DesignerRestoreBody> & { expected_updated_at?: string },
): Promise<unknown> {
  const { data } = await givmoClient.patch({
    url: one(orgRef, fundraiserId, emailId),
    body,
    throwOnError: true,
  });
  return data;
}

export async function restoreDesignerEmail(
  orgRef: string,
  fundraiserId: string,
  emailId: string,
  body: DesignerRestoreBody,
): Promise<unknown> {
  const { data } = await givmoClient.patch({
    url: one(orgRef, fundraiserId, emailId),
    body,
    throwOnError: true,
  });
  return data;
}

/** One copy to the caller's own confirmed sign-in address; the server takes no address. */
export async function sendDesignerTest(
  orgRef: string,
  fundraiserId: string,
  emailId: string,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${one(orgRef, fundraiserId, emailId)}/test`,
    body: {},
    throwOnError: true,
  });
  return data;
}
